import React from "react";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { FiArrowLeft } from "react-icons/fi";
import { useLanguage } from "../../contexts/LanguageContext";
import { BRAND_NAME } from "../../lib/constants";
import { useAuthStore } from "../../store/authStore";
import LegalLinks from "../../legal/LegalLinks";

const Wrap = styled.div`
  min-height: 100vh;
  background: ${(p) => p.theme.colors.background};
  color: ${(p) => p.theme.colors.text};
`;
const Bar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  max-width: 820px;
  margin: 0 auto;
  padding: 16px 24px;
  img {
    height: 36px;
    width: auto;
    display: block;
  }
  a,
  button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    color: ${(p) => p.theme.colors.textSecondary};
    &:hover {
      color: ${(p) => p.theme.colors.text};
    }
  }
`;
const Article = styled.main`
  max-width: 820px;
  margin: 0 auto;
  padding: 16px 24px 64px;
  h1 {
    font-size: clamp(26px, 4vw, 36px);
    letter-spacing: -0.03em;
    line-height: 1.2;
    margin-bottom: 24px;
  }
  p {
    margin-bottom: 16px;
    line-height: 1.7;
    color: ${(p) => p.theme.colors.textSecondary};
    max-width: 70ch;
  }
  h2 {
    font-size: clamp(18px, 2.6vw, 22px);
    line-height: 1.3;
    margin: 32px 0 12px;
  }
  h3 {
    font-size: 16px;
    margin: 20px 0 8px;
  }
  ul,
  ol {
    margin: 0 0 16px;
    padding-left: 24px;
    max-width: 70ch;
    line-height: 1.7;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  li {
    margin-bottom: 6px;
    overflow-wrap: anywhere;
  }
  hr {
    border: 0;
    border-top: 1px solid ${(p) => p.theme.colors.border};
    margin: 24px 0;
  }
  strong {
    color: ${(p) => p.theme.colors.text};
  }
  p {
    overflow-wrap: anywhere;
  }
  a {
    color: ${(p) => p.theme.colors.primary};
    text-decoration: underline;
  }
  @media (max-width: 480px) {
    padding: 8px 16px 48px;
  }
`;

export default function LegalPage({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  const nav = useNavigate();
  const { t } = useLanguage();
  const authenticated = useAuthStore((s) => s.isAuthenticated);
  return (
    <Wrap>
      <Bar>
        <Link to="/" aria-label={BRAND_NAME}>
          <img src="/cs-hub-logo.png" alt={BRAND_NAME} />
        </Link>
        <button
          type="button"
          onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
        >
          <FiArrowLeft aria-hidden="true" />
          {t("common.back")}
        </button>
      </Bar>
      <Article>
        <h1>{title}</h1>
        {children}
        {!authenticated && (
          <p>
            <Link to="/register">{t("legal.to_register")}</Link>
          </p>
        )}
        <LegalLinks />
      </Article>
    </Wrap>
  );
}
